import { useEffect, useMemo, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { fetchGroupShare } from '../lib/shareApi'
import { cdnUrl } from '../lib/cdnUrl'
import { SP, openInApp, deeplinkFor, attemptOpenApp } from '../lib/shareTokens'
import { usePageMeta } from '../lib/usePageMeta'
import type { GroupShare, UpcomingEvent } from '../types/share'
import { STRINGS } from '../lib/i18n'
import { applyModeOf } from '../lib/registrationMode'
import SmartBanner from '../components/share/SmartBanner'
import Hero from '../components/share/Hero'
import Wordmark from '../components/share/Wordmark'
import StationMark from '../components/share/StationMark'
import StickyCTA from '../components/share/StickyCTA'

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// This page is not localised yet (ON-333 covered the event page only), so it
// reads the English table rather than keeping a second copy of it.
const EVENT_TYPE_LABEL = STRINGS.en.eventTypes
const MODE_BADGE = STRINGS.en.modeBadge

function formatEventDate(iso: string) {
  const d = new Date(iso)
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: d.toLocaleString('en', { month: 'short' }).toUpperCase(),
    weekday: d.toLocaleString('en', { weekday: 'short' }).toUpperCase(),
    time: d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: true }),
  }
}

function activeDayIndices(schedule: GroupShare['schedule']): number[] {
  if (!schedule?.days) return []
  return schedule.days
    .map(d => DAY_NAMES.findIndex(n => n.toLowerCase() === d.toLowerCase()))
    .filter(i => i !== -1)
}

export default function GroupSharePage() {
  const { id } = useParams<{ id: string }>()
  // Read through useLocation (as EventSharePage does) so the hand-off links
  // follow the router's view of the query string.
  const { search } = useLocation()
  const [group, setGroup] = useState<GroupShare | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    fetchGroupShare(id)
      .then(setGroup)
      .catch(() => setError('Could not load group'))
      .finally(() => setLoading(false))
  }, [id])

  const heroImageUrl = useMemo(() => {
    if (!group) return undefined
    return group.hero_image_path ? cdnUrl('group-images', group.hero_image_path) : undefined
  }, [group])

  usePageMeta(group ? {
    title: group.name,
    description: group.description ?? `${group.member_count} members · Stazon running club`,
    imageUrl: heroImageUrl,
    iosAppId: import.meta.env.VITE_IOS_APP_STORE_ID,
  } : null)

  useEffect(() => {
    // ON-455: `search` carries the link's query string (ref/src tags,
    // open=chat, ...) onto the hand-off.
    // Not a dependency — the attempt is once per session and must not re-fire
    // on a query-only navigation.
    if (group?.id) attemptOpenApp('group', group.id, search)
  }, [group?.id])

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: SP.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: `3px solid ${SP.border}`,
          borderTopColor: SP.lime,
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (error || !group) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: SP.bg, color: SP.text, fontFamily: SP.ui, WebkitFontSmoothing: 'antialiased' }}>
        <SmartBanner onOpen={() => {}} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 57px)', padding: '0 40px', textAlign: 'center' }}>
          <StationMark size={44} color={SP.dim} style={{ marginBottom: 24, opacity: 0.4 }} />
          <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.8, margin: '0 0 12px', color: SP.text }}>Group not found</h2>
          <p style={{ fontSize: 15, color: SP.muted, fontFamily: SP.body, fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
            {error ?? 'This group is not available or may have been removed.'}
          </p>
        </div>
        <div style={{ position: 'absolute', bottom: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <Wordmark height={16} color={SP.dim} />
        </div>
      </div>
    )
  }

  const activeIndices = activeDayIndices(group.schedule)
  const handleOpen = () => openInApp('group', group.id, search)

  const foundedYear = new Date(group.created_at).toLocaleString('en', { month: 'short', year: 'numeric' })
  const typeLabel = group.group_types?.length ? group.group_types[0].replace(/_/g, ' ') : 'Club'

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', background: SP.bg, zIndex: 100 }}>
      <div style={{ minHeight: '100%', background: SP.bg, color: SP.text, fontFamily: SP.ui, WebkitFontSmoothing: 'antialiased' }}>
        <SmartBanner onOpen={handleOpen} deeplink={deeplinkFor('group', group.id, search)} />

        <Hero imageUrl={heroImageUrl} height={380}>
          <div style={{ position: 'absolute', top: 20, left: 20 }}>
            <Wordmark height={18} />
          </div>
          <div style={{
            position: 'absolute', top: 20, right: 20,
            padding: '6px 12px', borderRadius: 999,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(14px)',
            border: `1px solid ${SP.borderBright}`,
            fontSize: 10, fontFamily: SP.mono, letterSpacing: 1.5, color: SP.lime,
          }}>
            {typeLabel.toUpperCase()} · OPEN
          </div>
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontFamily: SP.mono, color: SP.lime, letterSpacing: 2.5, textTransform: 'uppercase', fontWeight: 600, marginBottom: 14 }}>
              <StationMark size={10} color={SP.lime} />
              RUNNING CLUB
            </div>
            <h1 style={{ fontSize: 40, fontWeight: 800, letterSpacing: -1.5, lineHeight: 0.95, color: SP.text, margin: 0 }}>
              {group.name}
            </h1>
            <div style={{ marginTop: 14, fontFamily: SP.body, fontStyle: 'italic', fontSize: 16, color: SP.muted }}>
              Since <span style={{ color: SP.text, fontStyle: 'normal', fontWeight: 600, fontFamily: SP.ui }}>{foundedYear}</span>
            </div>
          </div>
        </Hero>

        {/* Stats */}
        <div style={{ margin: '0 20px', padding: '22px 0', borderBottom: `1px solid ${SP.border}`, display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: SP.mono, fontSize: 24, color: SP.lime, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
              {group.member_count}
            </div>
            <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>Members</div>
          </div>
          {group.schedule?.days?.length ? (
            <div style={{ flex: 1, paddingLeft: 12, borderLeft: `1px solid ${SP.border}` }}>
              <div style={{ fontFamily: SP.mono, fontSize: 24, color: SP.text, fontWeight: 500, lineHeight: 1, letterSpacing: -0.5 }}>
                {group.schedule.days.length}×
              </div>
              <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>Per week</div>
            </div>
          ) : null}
          {group.schedule?.time && (
            <div style={{ flex: 1, paddingLeft: 12, borderLeft: `1px solid ${SP.border}` }}>
              <div style={{ fontFamily: SP.mono, fontSize: 24, color: SP.text, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                {group.schedule.time}
              </div>
              <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>Start time</div>
            </div>
          )}
        </div>

        {/* Bio */}
        {group.description && (
          <section style={{ padding: '36px 20px 24px' }}>
            <div style={{ position: 'relative', paddingLeft: 20 }}>
              <div style={{
                position: 'absolute', left: 0, top: 6, bottom: 6, width: 3,
                background: SP.lime, borderRadius: 2, boxShadow: `0 0 10px ${SP.lime}88`,
              }} />
              <p style={{ fontFamily: SP.body, fontStyle: 'italic', fontSize: 19, lineHeight: 1.4, color: SP.text, margin: 0, letterSpacing: -0.2 }}>
                {group.description}
              </p>
            </div>
          </section>
        )}

        {/* Schedule grid */}
        {activeIndices.length > 0 && (
          <section style={{ padding: '32px 20px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 2.5, textTransform: 'uppercase' }}>When we run</div>
            </div>
            <div data-testid="schedule-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
              {DAY_LABELS.map((d, i) => {
                const active = activeIndices.includes(i)
                return (
                  <div key={i} style={{
                    aspectRatio: '1 / 1.2',
                    borderRadius: 10,
                    background: active ? SP.lime : SP.card,
                    border: `1px solid ${active ? SP.lime : SP.border}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: active ? SP.charcoal : SP.muted,
                    boxShadow: active ? `0 8px 18px ${SP.lime}44` : 'none',
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 800, fontFamily: SP.ui }}>{d}</div>
                    {active && <div style={{ fontSize: 9, fontFamily: SP.mono, marginTop: 2, letterSpacing: 1 }}>RUN</div>}
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* Upcoming events */}
        {group.upcoming_events?.length > 0 && (
          <section style={{ padding: '32px 20px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 2.5, textTransform: 'uppercase' }}>Upcoming runs</div>
              <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1 }}>NEXT {group.upcoming_events.length}</div>
            </div>
            {group.upcoming_events.map((ev: UpcomingEvent, i: number) => {
              const { day, month, weekday, time } = formatEventDate(ev.event_datetime)
              const applyMode = applyModeOf(ev.registration_mode)
              const spotsLabel = ev.max_participants != null
                ? `${ev.current_registrations}/${ev.max_participants}`
                : `${ev.current_registrations} joined`
              return (
                <div key={ev.id} style={{
                  padding: '16px 0',
                  borderTop: i === 0 ? `1px solid ${SP.border}` : 'none',
                  borderBottom: `1px solid ${SP.border}`,
                  display: 'flex', gap: 14, alignItems: 'center',
                }}>
                  <div style={{
                    width: 54, padding: '6px 0', borderRadius: 10,
                    background: SP.card, border: `1px solid ${SP.border}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    flexShrink: 0,
                  }}>
                    <div style={{ fontSize: 9, fontFamily: SP.mono, color: SP.lime, letterSpacing: 1.5, fontWeight: 700 }}>{weekday}</div>
                    <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.text, fontWeight: 500, fontVariantNumeric: 'tabular-nums', lineHeight: 1, marginTop: 2 }}>{day}</div>
                    <div style={{ fontSize: 8, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1, marginTop: 2 }}>{month}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: SP.text, letterSpacing: -0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{ev.title}</div>
                      {applyMode && (
                        <span data-testid="upcoming-mode-badge" style={{
                          flexShrink: 0, padding: '2px 7px', borderRadius: 999,
                          border: `1px solid ${SP.lime}`, color: SP.lime,
                          fontSize: 9, fontFamily: SP.mono, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                        }}>
                          {MODE_BADGE[applyMode]}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: SP.muted, fontFamily: SP.mono, marginTop: 3, letterSpacing: 0.3 }}>
                      {EVENT_TYPE_LABEL[ev.event_type] ?? ev.event_type} · {ev.difficulty_level}
                    </div>
                    <div style={{ fontSize: 11, color: SP.dim, fontFamily: SP.mono, marginTop: 2, letterSpacing: 0.5 }}>{time}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: SP.mono, fontSize: 13, color: SP.text, fontVariantNumeric: 'tabular-nums' }}>{spotsLabel}</div>
                    <div style={{ fontSize: 9, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1, marginTop: 2 }}>JOINED</div>
                  </div>
                </div>
              )
            })}
          </section>
        )}

        {/* Footer */}
        <div style={{ padding: '40px 20px 20px', textAlign: 'center', borderTop: `1px solid ${SP.border}`, marginTop: 30 }}>
          <Wordmark height={20} />
        </div>

        <StickyCTA variant="group" onOpen={handleOpen} />
      </div>
    </div>
  )
}
